import { UppyFile } from '@uppy/core';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { PUBLIC_REPORT } from 'src/api';
import axios from 'src/axios';
import { Center, Spinner } from 'src/components/atoms';
import {
  FileUpload,
  handleErrorToast,
  UploadedEvidence,
  useFileUploadContext,
} from 'src/components/molecules';
import {
  AddUploadsToReportMutation,
  UploadSource,
  UploadType,
} from 'src/graphql';
import theme from 'src/theme/theme';
import { addingFilesAsEvidenceIntercomArticle } from 'src/utils/intercomArticles';
import i18next from '../../../i18n';
import * as Types from '../../../types';
import { getReadableIdWithDash } from '../../../types';
import { StyledIcon } from '../../foundations/foundations.styles';

import { useUploadReportDocumentation } from 'src/components/molecules/FileUpload/hooks/useUploadReportDocumentation';
import { UploadedEvidenceFile } from 'src/components/molecules/UploadedEvidence/types';
import { PublicLoggedInContainerWithNavbar } from 'src/components/organismsLegacy/LoggedInContainer/PublicLoggedInContainerWithNavbar';
import { BackTextLink } from '../PublicReport/PublicReport.styles';
import { IntercomLink } from '../questions/EvidenceQuestion/EvidenceQuestion.styles';
import {
  AdditionalEvidenceHeader,
  DescriptionContainer,
  FileUploadContainer,
  Header,
  PublicMoreEvidenceContainer,
  StyledIconContainer,
} from './PublicMoreEvidence.styles';

interface Props {
  organisation: string;
  currentUser: Types.User;
  reportId: string;
  publicReports: Types.Report[];
  isReporterViewEvidenceDisabled?: boolean;
}

export const PublicMoreEvidence = (props: Props) => {
  const { t } = useTranslation();
  const displayId = getReadableIdWithDash(props.reportId);
  const { handleFileRemove } = useFileUploadContext();
  const [report, setReport] = useState<Types.PublicReport | null>(null);
  const [loading, setLoading] = useState(true);

  const caseIsNotClosed = useMemo(() => {
    return report?.status !== 'CLOSED';
  }, [report]);
  const isVaultTalk = props.publicReports[0].source === 'vault-talk';

  const header = isVaultTalk
    ? t('public.PublicMoreEvidence.vaultTalkOpenCaseHeader')
    : t('public.PublicMoreEvidence.header');

  const onUploadSuccess = (
    uploads: AddUploadsToReportMutation['addUploadsToReport'],
  ) => {
    setReport((prevReport) => {
      if (!prevReport) {
        return null;
      }
      if (!uploads) {
        return prevReport;
      }
      return {
        ...prevReport,
        postSubmissionFiles: [
          ...(prevReport.postSubmissionFiles || []),
          ...(uploads as UploadType[]),
        ],
      };
    });
  };

  const { uploadFilesToReportPostSubmission } = useUploadReportDocumentation({
    uploadSource: UploadSource.OprPostsubmission,
    onPostSubmissionUploadSuccess: onUploadSuccess,
  });

  const onConfirm = async (files: UppyFile[]) => {
    const { successfulFileUploads } = await uploadFilesToReportPostSubmission({
      files: files,
      reportId: props.reportId,
    });

    successfulFileUploads.forEach((file) => {
      handleFileRemove(file.id);
    });
  };

  useEffect(() => {
    const getReport = async () => {
      try {
        const resp = await axios.get(PUBLIC_REPORT(props.reportId));
        setReport(resp.data.publicReport);
      } catch (error) {
        handleErrorToast({ toastMessage: 'Report failed to load.' });
      }

      setLoading(false);
    };
    getReport();
  }, [props.reportId]);

  const uploadedFiles: UploadedEvidenceFile[] =
    report?.postSubmissionFiles?.map((file) => ({
      size: file.size,
      dateAdded: file.createdAt,
      fileExtension: file.fileExtension,
      fileS3Path: file.s3path,
      status: file.scanStatus as Types.UploadScanStatus,
      dataDeletedAt: file.dataDeletedAt,
      dataDeletedBy: file.dataDeletedBy,
    })) || [];

  const showUploadedFiles = Boolean(uploadedFiles.length) && Boolean(report);

  const linkComponentToBeEmbedded = (
    <IntercomLink
      href={addingFilesAsEvidenceIntercomArticle}
      size="medium"
      target="_blank"
      rel="no-referrer"
      data-testid="file-upload-help-article-link"
    />
  );
  return (
    <PublicLoggedInContainerWithNavbar
      organisation={props.organisation}
      currentUser={props.currentUser}
      selectedTabIndex={1}
      hideMenusWhenSmall
      hideID={props.publicReports.every(
        (report) => report.source === 'vault-talk',
      )}
    >
      <PublicMoreEvidenceContainer>
        <BackTextLink
          to={`/${props.organisation}/open-reporting/reports`}
          data-testid={'back-to-reports'}
        >
          <StyledIconContainer>
            <StyledIcon
              size="24px"
              color={theme.newPalette.turquoise.T300}
              className={'uil uil-arrow-left'}
            />
          </StyledIconContainer>
          {t('public.PublicMoreEvidence.backToReportId', {
            reportId: displayId,
          })}
        </BackTextLink>

        {loading && (
          <Center>
            <Spinner variant="secondary" size="xlarge" />
          </Center>
        )}

        {!loading && caseIsNotClosed && (
          <>
            <Header>{header}</Header>
            <DescriptionContainer>
              <Trans
                i18n={i18next}
                i18nKey="public.PublicMoreEvidence.description"
                components={{
                  a: linkComponentToBeEmbedded,
                }}
              />
            </DescriptionContainer>
            <FileUploadContainer>
              <FileUpload
                dragDropText={t('public.FileUpload.dragDrop')}
                highlightedText={t('public.FileUpload.highlighted')}
                fileLimitText={t('public.FileUpload.fileLimit')}
                informationText={t('public.FileUpload.information')}
                addFilesButtonText={t('public.FileUpload.addFiles')}
                tooltipFileOptions={[
                  {
                    type: t('public.FileUpload.tooltipImage'),
                    options: t('public.FileUpload.tooltipImageOptions'),
                  },
                  {
                    type: t('public.FileUpload.tooltipText'),
                    options: t('public.FileUpload.tooltipTextOptions'),
                  },
                  {
                    type: t('public.FileUpload.tooltipVideo'),
                    options: t('public.FileUpload.tooltipVideoOptions'),
                  },
                  {
                    type: t('public.FileUpload.tooltipAudio'),
                    options: t('public.FileUpload.tooltipAudioOptions'),
                  },
                  {
                    type: t('public.FileUpload.tooltipDocuments'),
                    options: t('public.FileUpload.tooltipDocumentsOptions'),
                  },
                ]}
                confirmButtonText={t('public.FileUpload.confirm')}
                uploadFilesButtonText={t('public.FileUpload.uploadFiles')}
                onConfirm={onConfirm}
              />
            </FileUploadContainer>
          </>
        )}
        {!loading && showUploadedFiles && (
          <>
            <AdditionalEvidenceHeader caseIsNotClosed={caseIsNotClosed}>
              {t('public.PublicMoreEvidence.additionalEvidenceHeader')}
            </AdditionalEvidenceHeader>
            <UploadedEvidence
              files={uploadedFiles}
              reportId={report?.id ?? ''}
              hideTitle
              localisedText={{
                tableTitle: t('public.Evidence.tableTitle'),
                webErrorMessage: t('public.Evidence.webErrorMessage'),
                mobileErrorMessage: t('public.Evidence.mobileErrorMessage'),
                nameHeading: t('public.Evidence.nameHeading'),
                addedHeading: t('public.Evidence.addedHeading'),
                statusHeading: t('public.Evidence.statusHeading'),
                failedToOpenFileMessage: t(
                  'public.Evidence.failedToOpenFileMessage',
                ),
                fileStatuses: {
                  pendingUpload: t(
                    'public.Evidence.fileStatuses.pendingUpload',
                  ),
                  passedUpload: t('public.Evidence.fileStatuses.passedUpload'),
                  infectedUpload: t(
                    'public.Evidence.fileStatuses.infectedUpload',
                  ),
                  readyForDownload: t(
                    'public.Evidence.fileStatuses.readyForDownload',
                  ),
                },
              }}
              baseFileName="AdditionalEvidence"
              downloadEvidenceDisabled={props.isReporterViewEvidenceDisabled}
            />
          </>
        )}
      </PublicMoreEvidenceContainer>
    </PublicLoggedInContainerWithNavbar>
  );
};
